/**
 * OAuth2 PKCE utility module.
 * Uses Web Crypto API for SHA-256 hashing -- no external dependencies.
 */

import { AUTH_CONFIG } from './config.js';

const VERIFIER_KEY = 'pkce_code_verifier';
const STATE_KEY = 'pkce_state';

// --- Base64url encoding ---

function base64urlEncode(buffer) {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// --- PKCE generators ---

export function generateCodeVerifier() {
	const buffer = new Uint8Array(96);
	crypto.getRandomValues(buffer);
	return base64urlEncode(buffer);
}

export async function generateCodeChallenge(verifier) {
	if (!crypto?.subtle) {
		throw new Error('Secure context required (HTTPS or localhost). PKCE unavailable.');
	}
	const encoder = new TextEncoder();
	const data = encoder.encode(verifier);
	const digest = await crypto.subtle.digest('SHA-256', data);
	return base64urlEncode(digest);
}

export function generateState() {
	const buffer = new Uint8Array(32);
	crypto.getRandomValues(buffer);
	return base64urlEncode(buffer);
}

// --- SessionStorage helpers ---

export function storeVerifier(verifier) {
	sessionStorage.setItem(VERIFIER_KEY, verifier);
}

export function getVerifier() {
	return sessionStorage.getItem(VERIFIER_KEY);
}

export function clearVerifier() {
	sessionStorage.removeItem(VERIFIER_KEY);
}

export function storeState(state) {
	sessionStorage.setItem(STATE_KEY, state);
}

export function getState() {
	return sessionStorage.getItem(STATE_KEY);
}

export function clearState() {
	sessionStorage.removeItem(STATE_KEY);
}

// --- Authorize URL builder ---

export async function buildAuthorizeUrl({ clientId, redirectUri, scopes } = {}) {
	const verifier = generateCodeVerifier();
	const challenge = await generateCodeChallenge(verifier);
	const state = generateState();

	storeVerifier(verifier);
	storeState(state);

	const params = new URLSearchParams({
		response_type: 'code',
		client_id: clientId || AUTH_CONFIG.clientId,
		redirect_uri: redirectUri || AUTH_CONFIG.redirectUri,
		scope: scopes || AUTH_CONFIG.scopes,
		code_challenge: challenge,
		code_challenge_method: 'S256',
		state,
	});

	return `${AUTH_CONFIG.authServerUrl}${AUTH_CONFIG.endpoints.authorize}?${params.toString()}`;
}
